import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Typography } from "@mui/material";
import {
  AddRounded,
  ArrowBackRounded,
  ShareOutlined,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import { DocumentCraftingDocumentDetail } from "../../../Models/documentCrafting";
import { useUpdateDocumentCraftingDocument } from "../../../Query/useDocumentCrafting";
import {
  DocumentTypeIcon,
  LanguageBadge,
  PersonalBadge,
  VisibilityIcon,
} from "../Common/DocumentCraftingUi";
import { documentCraftingPaths } from "../Common/documentCraftingNavigation";
import { formatDate, useUserName } from "../Common/format";
import { useDocumentCraftingEnumLabels } from "../Common/useDocumentCraftingEnumLabels";
import DocumentActionsMenu, { DocumentAction } from "./DocumentActionsMenu";
import DocumentLabels from "./DocumentLabels";
import EditableTitle from "./EditableTitle";

const Meta: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      fontSize: 13,
      color: color.gray600,
    }}
  >
    {children}
  </Box>
);

interface Props {
  document: DocumentCraftingDocumentDetail;
  /** New revision from the revision on screen (editors, saved revisions). */
  onNewRevision?: () => void;
  onShare?: () => void;
  actions: DocumentAction[];
}

/** Title (editable), type, language, owner, visibility (or the personal
 * badge), labels, sharing and the actions of the document. */
const DocumentHeader: React.FC<Props> = ({
  document,
  onNewRevision,
  onShare,
  actions,
}) => {
  const { t, i18n } = useTranslation("documentCrafting");
  const navigate = useNavigate();
  const labels = useDocumentCraftingEnumLabels();
  const userName = useUserName();
  const update = useUpdateDocumentCraftingDocument();

  return (
    <Box sx={{ mb: 3 }}>
      <Button
        size="small"
        color="secondary"
        startIcon={<ArrowBackRounded />}
        onClick={() => navigate(documentCraftingPaths.list)}
        sx={{ mb: 1, ml: -1 }}
      >
        {t("nav.backToList")}
      </Button>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", md: "flex-start" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <EditableTitle
            title={document.title}
            canEdit={document.permissions.can_edit}
            pending={update.isPending}
            onSave={(title) =>
              update.mutateAsync({ documentId: document.id, data: { title } })
            }
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              columnGap: 2,
              rowGap: 0.75,
              mt: 0.75,
            }}
          >
            <Meta>
              <DocumentTypeIcon typeKey={document.type_key} size={18} tooltip={false} />
              {labels.type(document.type_key)}
            </Meta>
            <Meta>
              <LanguageBadge language={document.language} />
              {labels.language(document.language)}
            </Meta>
            {document.is_personal ? (
              <PersonalBadge />
            ) : (
              <Meta>
                <VisibilityIcon
                  visibility={document.visibility}
                  shared={document.share_count > 0}
                  size={16}
                />
                {labels.visibility(document.visibility)}
              </Meta>
            )}
            <Meta>
              {t("detail.header.owner", { name: userName(document.owner) })}
            </Meta>
            <Meta>
              <Typography component="span" sx={{ fontSize: 13, color: color.gray500 }}>
                {t("detail.header.created", {
                  name: userName(document.created_by),
                  date: formatDate(document.created_at, i18n.language),
                })}
              </Typography>
            </Meta>
          </Box>
          <Box sx={{ mt: 1.25 }}>
            <DocumentLabels document={document} />
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", flexShrink: 0 }}>
          {onShare && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ShareOutlined />}
              onClick={onShare}
            >
              {t("detail.header.share")}
            </Button>
          )}
          <DocumentActionsMenu actions={actions} />
          {onNewRevision && (
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={onNewRevision}
            >
              {t("detail.actions.newRevision")}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DocumentHeader;
